import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

export function Field({ label, name, required, hint, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={name} className="text-sm font-medium text-navy">
        {label} {required && <span className="text-ruby">*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function TextField({ label, name, required, hint, ...props }) {
  return (
    <Field label={label} name={name} required={required} hint={hint}>
      <Input id={name} name={name} data-testid={`field-${name}`}
        className="bg-white border-border focus-visible:ring-emerald" {...props} />
    </Field>
  );
}

export function AreaField({ label, name, required, hint, rows = 4, ...props }) {
  return (
    <Field label={label} name={name} required={required} hint={hint}>
      <Textarea id={name} name={name} rows={rows} data-testid={`field-${name}`}
        className="bg-white border-border focus-visible:ring-emerald" {...props} />
    </Field>
  );
}

export function SelectField({ label, name, required, value, onValueChange, placeholder, options, hint }) {
  return (
    <Field label={label} name={name} required={required} hint={hint}>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger data-testid={`field-${name}`} className="bg-white border-border focus:ring-emerald">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="bg-white">
          {options.map((o) => (
            <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function ConsentBox({ checked, onChange, name = "consent", children }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={`field-${name}`}
        className="mt-0.5 data-[state=checked]:bg-emerald data-[state=checked]:border-emerald" />
      <span className="text-sm text-muted-foreground leading-relaxed">{children}</span>
    </label>
  );
}

// Hidden honeypot — real users never fill this. Bots often do.
export function Honeypot({ value, onChange }) {
  return (
    <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }}>
      <label>Do not fill this field
        <input tabIndex={-1} autoComplete="off" name="hp_field" value={value} onChange={(e) => onChange(e.target.value)} />
      </label>
    </div>
  );
}

export function Eyebrow({ children }) {
  return (
    <span className="inline-block text-xs font-semibold uppercase tracking-[0.22em] text-gold-dark">
      {children}
    </span>
  );
}
